<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { useRgStore } from '~/stores/responsibleGaming.store';
import { useBonusService } from '~/composables/services/useBonusService';
import type { CasinoBonus } from '~/interfaces/dto/bonus/response';
import Banking from '~/components/banking/banking.vue';
import { useUserConfig } from '~/composables/useCheckRestrictions';
import { ESumSubAction } from '~/interfaces/dto/compliance/request';
import AccountRestricted from '~/components/account/responsible-gaming/account-restricted.vue';

const authStore = useAuthStore();
const site = useSiteStore();
const rg = useRgStore();
const bonusService = useBonusService();

const { $enabled, $t, $trackEvent } = useNuxtApp();

const displayWithdrawalVerificationSumsub = ref<boolean>(false);

const withdrawalRestricted = computed(() => {
  if ($enabled('myaccount.enablepartialreg'))
    if (rg.hasPartialRegLock) return !rg.restrictionPermissions.allowedWager;
  return !rg?.restrictionPermissions.allowedWithdrawal;
});
const withdrawalVerification = computed(
  () => rg.withdrawalVerificationRequired,
);

async function refetchRestrictions() {
  displayWithdrawalVerificationSumsub.value = false;
  await useUserConfig();
}

onBeforeMount(() => {
  if (
    authStore.getComplianceStatus < 1 &&
    $enabled('myaccount.enablecompliance') &&
    !$enabled('myaccount.enablepartialreg')
  ) {
    authStore.setAccountModalPage('document-verification');
  } else {
    $trackEvent('Account', 'MY_ACCOUNT_TELLER', {
      to: 'withdrawal',
    });
    doWithdrawalChecks();
  }
  useUserConfig();
});
function doWithdrawalChecks() {
  if (withdrawalRestricted.value && !withdrawalVerification.value) {
    setTimeout(() => {
      authStore.toggleAccountModal(false);
    }, 250);
    site.activateModal('accountRestricted');
  } else {
    getBonusStatus();
  }
}

function getBonusStatus() {
  try {
    bonusService.getBonusStack().then((data) => {
      if (data) {
        if (
          data?.activeCasinoBonus?.totalAwardedBonusAmount ||
          checkForPendingBonuses(data?.pendingCasinoBonuses)
        ) {
          site.activateModal('withdrawalAlert');
        }
      }
      return;
    });
  } catch (e) {
    console.error(e);
  }
}

function checkForPendingBonuses(pendingBonuses: CasinoBonus[]) {
  if (pendingBonuses.length > 0) {
    for (let i = 0; i < pendingBonuses.length; i++) {
      if (pendingBonuses[i]?.totalAwardedBonusAmount > 0) {
        return true;
      }
    }
  }
  return false;
}

watchEffect(() => {
  if (displayWithdrawalVerificationSumsub.value && !withdrawalRestricted.value)
    displayWithdrawalVerificationSumsub.value = false;
});
</script>
<template>
  <div>
    <UserLimitedFunctionalityHolster
      :validate-partial-reg="$enabled('myaccount.enablepartialreg')"
      :validate-compliance="$enabled('myaccount.enablecompliance')"
      notice-key="withdrawal"
      site-section="banking"
    >
      <section>
        <div class="flex items-center w-full">
          <LazyUserAccountSettingsHeader
            :title="`${$t('withdrawal')} (${
              authStore.currentUser.primaryMobileNumber?.mobileNumber
            })`"
            show-balance-mobile
            return-route="teller"
          />
          <div class="px-3 hidden md:block">
            <UserBalances
              class="w-full"
              display-balance-toggle
              display-cash-balance
              display-bonus-balance
              stretch
              account-options-modal-style
              single-line
            />
          </div>
        </div>
        <UserSumsub
          v-model="displayWithdrawalVerificationSumsub"
          :action="ESumSubAction.WithdrawAction"
          @complete="refetchRestrictions"
        >
          <AccountRestricted
            v-if="withdrawalVerification"
            class="md:max-w-[50%] mx-auto"
          >
            <template #restrictionTitle>
              {{ $t('withdrawal-verification-title') }}
            </template>
            <template #restrictionMessage>
              {{ $t('withdrawal-verification-message') }}
            </template>
            <template #action>
              <DesignSystemButton
                class="mx-auto"
                @click="displayWithdrawalVerificationSumsub = true"
              >
                {{ $t('verify-now') }}
              </DesignSystemButton>
            </template>
          </AccountRestricted>

          <Banking v-else type="Withdrawals" />
        </UserSumsub>
      </section>
    </UserLimitedFunctionalityHolster>
  </div>
</template>
